import { t, useI18n } from '../../i18n';
import React, { useEffect, useId, useRef, useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import {
  assignableRoles,
  roleService,
  type CompanyRole,
  type StaffAccess,
} from '../../services/roleService';
import {
  staffService,
  type ContractHoursBand,
  type StaffInviteResult,
} from '../../services/staffService';
import { sanitizeEmail, sanitizeText } from '../../utils/sanitize';
import ContractHoursSelect from './ContractHoursSelect';
import RolePicker from './RolePicker';

export interface StaffInviteOutcome extends StaffInviteResult {
  /** False when the invite went out but the contract hours chosen could not be saved. */
  contractHoursSaved: boolean;
}

interface Props {
  access: StaffAccess;
  onInvited: (outcome: StaffInviteOutcome) => void;
  onClose: () => void;
}

const MAX_NAME_LENGTH = 80;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Most new starters are guards, so the entry-level rank is where the form begins. */
const initialSelection = (roles: CompanyRole[]): string[] => {
  const entryRank = roles.find((role) => role.systemKey === 'guard_rank_1')
    ?? roles.find((role) => role.kind === 'guard');
  return entryRank ? [entryRank.id] : [];
};

export default function AddStaffModal({ access, onInvited, onClose }: Props) {
  useI18n();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<CompanyRole[] | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [contractHours, setContractHours] = useState<ContractHoursBand | null>(null);
  const [showContractError, setShowContractError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstNameRef = useRef<HTMLInputElement | null>(null);
  useDialogFocus(dialogRef);

  // The dialog takes focus first so Tab is trapped; the first field is where
  // typing should start.
  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  // Somebody who is not a system administrator adds guards. Who else may open
  // the workspace is a system administrator's decision, so they are only
  // offered guard roles. The database refuses the rest either way.
  useEffect(() => {
    let cancelled = false;
    void roleService.fetchRoles()
      .then((all) => {
        if (cancelled) return;
        const offered = assignableRoles(access, all);
        setRoles(offered);
        setRoleIds(initialSelection(offered));
      })
      .catch((err) => {
        if (cancelled) return;
        setRoles([]);
        setError((err as Error).message || t('Could not load roles.'));
      });
    return () => {
      cancelled = true;
    };
  }, [access]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isSubmitting) onClose();
  };

  const selectedRoles = (roles ?? []).filter((role) => roleIds.includes(role.id));
  const opensWorkspace = selectedRoles.some((role) => role.kind === 'management');
  const worksShifts = selectedRoles.some((role) => role.kind === 'guard');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const cleanFirstName = sanitizeText(firstName, MAX_NAME_LENGTH);
    const cleanLastName = sanitizeText(lastName, MAX_NAME_LENGTH);
    const cleanEmail = sanitizeEmail(email);

    if (!cleanFirstName || !cleanLastName) {
      setError(t('First name and last name are required'));
      return;
    }
    if (!EMAIL_PATTERN.test(cleanEmail)) {
      setError(t('A valid email address is required'));
      return;
    }
    if (roleIds.length === 0) {
      setError(t('Choose at least one role'));
      return;
    }
    // The scheduler gives guards their contracted hours, so it has to know them from day one.
    if (worksShifts && !contractHours) {
      setShowContractError(true);
      setError(t('Choose their contract hours'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const result = await staffService.inviteStaff({
        firstName: cleanFirstName,
        lastName: cleanLastName,
        email: cleanEmail,
        roleIds,
      });

      // The profile exists from the moment the invite is created, so the band
      // can go on it straight away. If that fails the invite still stands and
      // the profile shows the band as missing, which is the way to put it right.
      let contractHoursSaved = true;
      if (worksShifts && contractHours) {
        try {
          await staffService.setContractHours(result.userId, contractHours);
        } catch {
          contractHoursSaved = false;
        }
      }
      onInvited({ ...result, contractHoursSaved });
    } catch (err) {
      setError((err as Error).message || t('Failed to send invite'));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className="modal staff-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="staff-form__head">
          <div>
            <h2 className="modal__title" id={titleId}>{t('Add user')}</h2>
            <p className="staff-form__lead">
              {t('We will email them a link to finish setting up their account. They join your staff list as soon as the invite is sent, so you can roster them before they accept.')}
            </p>
          </div>
          <button
            type="button"
            className="staff-form__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t('Close')}
          >
            ✕
          </button>
        </div>

        <div aria-live="polite">
          {error && <div className="msg--error">{error}</div>}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="staff-form__grid">
            <div className="form__group">
              <label className="form__label" htmlFor="staff-form-first-name">{t('First name')}</label>
              <input
                id="staff-form-first-name"
                ref={firstNameRef}
                type="text"
                className="form__input"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                autoComplete="off"
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
            <div className="form__group">
              <label className="form__label" htmlFor="staff-form-last-name">{t('Last name')}</label>
              <input
                id="staff-form-last-name"
                type="text"
                className="form__input"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                autoComplete="off"
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-form-email">{t('Email')}</label>
            <input
              id="staff-form-email"
              type="email"
              className="form__input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="off"
              placeholder={t('name@company.com')}
            />
          </div>

          <div className="form__group">
            <span className="form__label">{t('Roles')}</span>
            {roles === null ? (
              <p className="staff-form__hint">{t('Loading roles...')}</p>
            ) : (
              <RolePicker
                idPrefix="staff-form"
                roles={roles}
                selectedIds={roleIds}
                onChange={setRoleIds}
                disabled={isSubmitting}
              />
            )}
            {selectedRoles.length > 0 && (
              <p className="staff-form__hint">
                {opensWorkspace && worksShifts
                  ? t('They will sign in to this workspace and work shifts from the mobile app.')
                  : opensWorkspace
                    ? t('They will sign in to this workspace. They will not be rostered as a guard.')
                    : t('They will work shifts and use the mobile app. Guards do not sign in to this workspace.')}
              </p>
            )}
          </div>

          {worksShifts && (
            <div className="form__group">
              <label className="form__label" htmlFor="staff-form-contract-hours">{t('Contract hours')}</label>
              <ContractHoursSelect
                id="staff-form-contract-hours"
                value={contractHours}
                onChange={(band) => {
                  setContractHours(band);
                  if (band) setShowContractError(false);
                }}
                disabled={isSubmitting}
                invalid={showContractError}
              />
              <p className="staff-form__hint">
                {t('The weekly hours they are guaranteed. Auto scheduling gives guards at least the bottom of their band where the rules allow.')}
              </p>
            </div>
          )}

          <div className="staff-form__footer">
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={isSubmitting}>
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn--primary staff-form__submit"
              disabled={isSubmitting || roles === null}
            >
              <FiSend aria-hidden="true" />
              {isSubmitting ? t('Sending invite...') : t('Send invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
